import { DiscountDiscountType, DiscountUnitType } from "../../dtos/offers";
import { UserInterfaceDto } from "../../dtos/user-interfaces";
import { WidgetDto, WidgetType } from "../../dtos/widgets";
import { RecursivePartial } from "../../utils";
import { MemCartItem } from "../plugin";

import {
  ResolvedWidgetDeviceConfigType,
  TextType,
  WidgetConfigType,
  WidgetDeviceConfigType,
  WidgetRenderType,
} from "./common";
import { MessageBoxVariant } from "./message-box-widget";
import {
  QuantityButtonsVariant,
  TypographySettings,
} from "./quantity-buttons-widget";

export const ProgressBarWidgetVariantList = [
  "progressBarWidget1",
  "progressBarWidget2",
  "progressBarWidget4",
  "progressBar1",
  "progressBar2",
  "progressBar3",
] as const;

export type ProgressBarWidgetVariant =
  (typeof ProgressBarWidgetVariantList)[number];

export const PROGRESS_BAR_VARIANT_NAMES: Record<
  ProgressBarWidgetVariant,
  string
> = {
  progressBar1: "Checkpoint classic",
  progressBar2: "Floating milestones",
  progressBar3: "Minimal",
  progressBarWidget1: "Progress bar",
  progressBarWidget2: "Progress bar",
  progressBarWidget4: "Progress bar",
};

export interface ProgressBarVariables {
  // legacy, by now these are more than textReplacements
  // we have to be backwards compatible though
  textReplacements: ProgressBarTextReplacements;
  subtotalQuerySelector: string | undefined;
  defaultShippingThreshold: number | undefined;
}

export interface ProgressBarTierVariables {
  buyMoreTemplate: string;
  buyMoreTemplateType: TextType;
  currentUnlockedTemplate: string;
  currentUnlockedTemplateType: TextType;
  tooltipTemplate: string;
  tooltipEnabled: boolean;
  activeIconId: BreakpointIconId;
  inactiveIconId: BreakpointIconId;
  activeBackgroundIconId: BreakpointBackgroundIconId;
  inactiveBackgroundIconId: BreakpointBackgroundIconId;
}

export interface ProgressBarTextReplacements extends ProgressBarTierVariables {
  unitName: string | undefined; // the name of your item
  more: string | undefined; // the "more" text
  allTiersUnlockedTemplate: string | undefined; // renders above the bar when ALL tiers unlocked
  tierVariables: Record<string, ProgressBarTierVariables>;
}

export interface ProgressBarStyles {
  container: {
    backgroundColor: string;
  };
  general: {
    cartTextPosition: "above" | "below";
  };
  layout: {
    swapTextPositions: boolean;
  };
  bar: {
    // TODO DM - kill this at some point
    backgroundColor: string;
    activeColor: string;
    inactiveColor: string;
    thickness: number;
    cornerRadius: number;
  };
  textColors: {
    dynamicProgressText: string;
    breakpointTextInactive: string;
    breakpointTextActive: string;
  };
  typography: {
    dynamicProgressText: TypographySettings;
    breakpointText: TypographySettings;
  };
  breakpoint: {
    activeColor: string;
    inactiveColor: string;
    activeIconColor: string;
    inactiveIconColor: string;
    size: number;
    iconSize: number;
    hideText: boolean;
  };
  tooltip: {
    backgroundColor: string;
  };
}

export type ProgressBarWidgetConfig = WidgetConfigType<
  ProgressBarWidgetVariant,
  ProgressBarStyles,
  ProgressBarVariables
>;

export type ProgressBarWidgetDeviceConfig = WidgetDeviceConfigType<
  ProgressBarWidgetVariant,
  ProgressBarStyles,
  ProgressBarVariables
>;

export type ResolvedProgressBarDeviceConfig = ResolvedWidgetDeviceConfigType<
  ProgressBarWidgetVariant,
  ProgressBarStyles,
  ProgressBarVariables
>;

export interface ResolvedProgressBarWidgetConfig {
  widgetType: WidgetType;
  desktop: ResolvedProgressBarDeviceConfig;
  mobile: ResolvedProgressBarDeviceConfig;
}

export interface ProgressBarWidgetDto extends WidgetDto {
  widgetType: "discountProgressBar" | "shippingProgressBar";
  config: ProgressBarWidgetConfig;
}

export interface ProgressBarConfig {
  unitType: DiscountUnitType;
  discountType: DiscountDiscountType;
  afterDiscount: boolean;
  isEntireStore: boolean;
  desktop: ProgressBarDeviceConfig;
  mobile: ProgressBarDeviceConfig;
}

export interface ProgressBarDeviceConfig {
  breakpoints: ProgressBarBreakpointConfig[];
  widget: ResolvedProgressBarDeviceConfig;
}

export interface ProgressBarBreakpointConfig {
  value: number;
  label?: string;
  tooltipEnabled?: boolean;
  completeText?: string;
  incompleteText?: string;
  reward: ProgressBarBreakpointReward;
  active: ProgressBarBreakpointState;
  inactive: ProgressBarBreakpointState;
}

export interface ProgressBarBreakpointState {
  iconId?: BreakpointIconId;
  backgroundIconId?: BreakpointBackgroundIconId;
}

export interface ProgressBarBreakpointReward {
  gwp?: boolean;
  freeShipping?: boolean;
  discount?: number;
}

export interface ProgressBarViewProps {
  formatter: ProgressBarCurrencyFormatter;
  config: ProgressBarConfig;
  cartValue?: number;
  renderType: WidgetRenderType;
}

export interface ProgressBarUserInterface
  extends Omit<
    UserInterfaceDto,
    "widget" | "desktopVariables" | "mobileVariables"
  > {
  widget?: ProgressBarWidgetDto;
  desktopVariables?: RecursivePartial<ProgressBarVariables>;
  mobileVariables?: RecursivePartial<ProgressBarVariables>;
}

export type BreakpointIconId =
  | "cart"
  | "cartAdd"
  | "check"
  | "crown"
  | "gift"
  | "key"
  | "label"
  | "lightning"
  | "lockClosed"
  | "lockClosedVersion1"
  | "lockOpen"
  | "plus"
  | "smileyDark"
  | "smileyLight"
  | "snowflake"
  | "star"
  | "sun"
  | "thumbsUp"
  | "truck";
export type BreakpointBackgroundIconId = "circle" | "star";

export type ProgressBarCurrencyFormatter = (
  price: number | string,
  hideCurrency?: boolean
) => string;

export interface ProgressBarWidgetType {
  updateOrBuild(
    containers: Element[],
    cartItems: MemCartItem[] | undefined
  ): Promise<boolean>;
  get widgetType(): WidgetType;
}
